import { useCallback, useEffect, useRef, useState } from 'react';
import { finalizeRecording, startRecording, uploadChunk } from './api.ts';

const MAX_ATTEMPTS = 3; // 1 initial attempt + 2 retries
const RETRY_DELAY_MS = 50;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Uploads one chunk, retrying transient failures a couple of times with a
// short backoff before propagating the failure.
async function uploadWithRetry(
  id: string,
  seq: number,
  blob: Blob,
  upload: (id: string, seq: number, blob: Blob) => Promise<void>,
): Promise<void> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      await upload(id, seq, blob);
      return;
    } catch (e) {
      lastErr = e;
      if (attempt < MAX_ATTEMPTS) await delay(RETRY_DELAY_MS * attempt);
    }
  }
  throw lastErr;
}

// Maintains an internal seq counter + a promise chain so chunks POST strictly
// in order — each enqueue awaits the previous upload before starting the
// next, even if MediaRecorder's `dataavailable` fires faster than uploads
// complete. flush() resolves (or rejects) once the whole chain has settled.
export function createChunkUploader(
  id: string,
  upload: (id: string, seq: number, blob: Blob) => Promise<void> = uploadChunk,
): { enqueue: (blob: Blob) => void; flush: () => Promise<void> } {
  let seq = 0;
  let chain: Promise<void> = Promise.resolve();

  const enqueue = (blob: Blob): void => {
    const thisSeq = seq++;
    chain = chain.then(() => uploadWithRetry(id, thisSeq, blob, upload));
    // Swallow so an unrelated later `.then` doesn't see an unhandled
    // rejection warning before flush() is awaited; flush() below still
    // surfaces the real rejection via the same chain reference.
    chain.catch(() => {});
  };

  const flush = (): Promise<void> => chain;

  return { enqueue, flush };
}

export type RecorderState = 'idle' | 'requesting' | 'recording' | 'paused' | 'processing' | 'error';

export interface UseRecorderResult {
  state: RecorderState;
  elapsedMs: number;
  analyser: AnalyserNode | null;
  error: string | null;
  start: () => Promise<void>;
  pause: () => void;
  resume: () => void;
  // Resolves with the terminal state stop() settled into ('processing' on
  // success, 'error' on a flush/finalize failure) — stop() never rejects, so
  // callers that need to react to a stop-time failure must branch on this
  // return value rather than assuming the state at call time.
  stop: () => Promise<'processing' | 'error'>;
}

// Hard cap on a single recording's duration; must match backend MAX_RECORDING_MS
// (config/recording.ts). At this many ms of *recording* time (paused time
// excluded) the hook auto-stops through the normal stop() path so the recording
// finalizes cleanly.
const MAX_RECORDING_MS = 5_400_000; // 90 min

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4'];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return undefined;
  return MIME_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t));
}

export function useRecorder(meetingId: string): UseRecorderResult {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const uploaderRef = useRef<ReturnType<typeof createChunkUploader> | null>(null);
  const recordingIdRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedBaseRef = useRef(0);
  const segmentStartRef = useRef(0);
  // Holds the latest stop() so the elapsed-time timer (set up in start/resume,
  // which are defined before stop) can trigger the auto-stop at the cap without
  // a stale closure. Guards against re-entrant auto-stop calls.
  const stopRef = useRef<(() => Promise<'processing' | 'error'>) | null>(null);
  const autoStoppingRef = useRef(false);

  // Recomputes accumulated RECORDING time (paused time excluded) and, once it
  // reaches the 90-min cap, auto-stops via the normal stop() path exactly once.
  const tick = useCallback(() => {
    const ms = elapsedBaseRef.current + (Date.now() - segmentStartRef.current);
    setElapsedMs(ms);
    if (ms >= MAX_RECORDING_MS && !autoStoppingRef.current) {
      autoStoppingRef.current = true;
      void stopRef.current?.();
    }
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cleanupMedia = useCallback(() => {
    clearTimer();
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    recorderRef.current = null;
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      void audioCtxRef.current.close();
    }
    audioCtxRef.current = null;
    setAnalyser(null);
  }, [clearTimer]);

  // Cleanup on unmount so a navigated-away recording doesn't leak the mic.
  useEffect(() => () => cleanupMedia(), [cleanupMedia]);

  const start = useCallback(async () => {
    setError(null);
    setState('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;

      const mimeType = pickMimeType() || 'audio/webm';
      const { id } = await startRecording(meetingId, mimeType);
      recordingIdRef.current = id;
      uploaderRef.current = createChunkUploader(id);

      const recorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });
      recorderRef.current = recorder;

      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyserNode = audioCtx.createAnalyser();
      source.connect(analyserNode);
      audioCtxRef.current = audioCtx;
      setAnalyser(analyserNode);

      recorder.addEventListener('dataavailable', (event: BlobEvent) => {
        if (event.data.size > 0) uploaderRef.current?.enqueue(event.data);
      });

      recorder.start(10_000);

      elapsedBaseRef.current = 0;
      segmentStartRef.current = Date.now();
      autoStoppingRef.current = false;
      setElapsedMs(0);
      clearTimer();
      timerRef.current = setInterval(tick, 250);

      setState('recording');
    } catch (e) {
      cleanupMedia();
      setError(e instanceof Error ? e.message : 'recording_start_failed');
      setState('error');
    }
  }, [meetingId, cleanupMedia, clearTimer, tick]);

  const pause = useCallback(() => {
    if (!recorderRef.current || recorderRef.current.state !== 'recording') return;
    recorderRef.current.pause();
    elapsedBaseRef.current += Date.now() - segmentStartRef.current;
    clearTimer();
    setState('paused');
  }, [clearTimer]);

  const resume = useCallback(() => {
    if (!recorderRef.current || recorderRef.current.state !== 'paused') return;
    recorderRef.current.resume();
    segmentStartRef.current = Date.now();
    clearTimer();
    timerRef.current = setInterval(tick, 250);
    setState('recording');
  }, [clearTimer, tick]);

  const stop = useCallback(async () => {
    const recorder = recorderRef.current;
    const uploader = uploaderRef.current;
    const id = recordingIdRef.current;
    clearTimer();
    try {
      if (recorder && recorder.state !== 'inactive') {
        await new Promise<void>((resolve) => {
          recorder.addEventListener('stop', () => resolve(), { once: true });
          recorder.stop();
        });
      }
      if (uploader) await uploader.flush();
      if (id) await finalizeRecording(id);
      setState('processing');
      return 'processing' as const;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'recording_stop_failed');
      setState('error');
      return 'error' as const;
    } finally {
      cleanupMedia();
    }
  }, [cleanupMedia, clearTimer]);

  // Keep the timer's stop reference pointed at the latest stop() so the
  // auto-stop at the duration cap always drives the current close path.
  stopRef.current = stop;

  return { state, elapsedMs, analyser, error, start, pause, resume, stop };
}
